/**
 * Écran « Comptes de paie » : sur quel compte comptable chaque ligne de la paie est écrite, pour cette entreprise.
 * Tout (libellés, comptes par défaut, référentiel v2, écarts) vient de `CORRESPONDANCE_COMPTES_PAIE` ;
 * aucun numéro de compte n'est écrit ici.
 */
import { esc } from '@presentation/shell/format';
import {
  ComptesPaieInvalideError,
  type SurchargesComptesPaie,
  type RoleComptePaie,
  type TableComptesPaie,
} from '@domain/comptabilite/ComptesPaie';
import {
  compteParDefaut,
  construireLignesComptesPaie,
  saisieDepuisTable,
  validerSaisieComptesPaie,
  type SaisieComptesPaie,
} from '@application/comptabilite/EcranComptesPaie';
import {
  STYLE_AIDE,
  STYLE_SCROLL,
  STYLE_TH_LIGNE,
  bandeauExpertHtml,
  erreurChampHtml,
  majErreurChamp,
  majResume,
  majStatut,
  type Confirmer,
  type Notifier,
} from './panelKit';

export const MESSAGE_APPLICATION_COMPTES_PAIE =
  "Le changement s'applique aux NOUVELLES écritures de paie. Les écritures déjà enregistrées ne sont pas modifiées ; pour corriger une période clôturée, le logiciel extourne et recomptabilise.";

export const DESCRIPTION_AUDIT_COMPTES_PAIE = 'Table des comptes de paie';

export interface ComptesPaieDeps {
  canMutate: boolean;
  load(): Promise<TableComptesPaie>;
  save(surcharges: SurchargesComptesPaie): Promise<TableComptesPaie>;
  audit(description: string, payload?: Record<string, unknown>): Promise<void>;
  confirm: Confirmer;
  notify: Notifier;
}

const P = 'cpt-paie';

export async function mountComptesPaiePanel(host: HTMLElement, deps: ComptesPaieDeps): Promise<void> {
  let table = await deps.load();

  const render = (): void => {
    const lignes = construireLignesComptesPaie(table);
    const corps = lignes
      .map((l) => {
        const idLib = `${P}-lib-${l.role}`;
        const idErr = `${P}-err-${l.role}`;
        const idAide = `${P}-aide-${l.role}`;
        const compte = deps.canMutate
          ? `<div class="field">
               <label class="sr-only" for="${P}-in-${l.role}">Compte utilisé pour « ${esc(l.libelle)} »</label>
               <input id="${P}-in-${l.role}" type="text" inputmode="numeric" autocomplete="off" maxlength="12"
                 data-compte-role="${l.role}" value="${esc(l.compteUtilise)}" class="mono"
                 aria-describedby="${idErr}" style="min-width:110px;">
               ${erreurChampHtml(idErr, undefined)}
             </div>`
          : `<strong class="mono">${esc(l.compteUtilise)}</strong>`;
        const restaurer = deps.canMutate
          ? `<button type="button" class="btn btn-ghost btn-sm" data-retablir="${l.role}"
               aria-label="Rétablir le compte par défaut pour « ${esc(l.libelle)} »">Rétablir ce compte</button>`
          : '';
        const aValider = l.aValider
          ? `<span class="pill warning"><span aria-hidden="true">⚠ </span>À valider</span>`
          : '';
        return `<tr data-role-ligne="${l.role}">
          <th scope="row" id="${idLib}" style="${STYLE_TH_LIGNE}">${esc(l.libelle)}${l.modifie ? ' <span class="pill info">Modifié</span>' : ''}</th>
          <td>${l.sens}</td>
          <td>${compte}</td>
          <td class="mono">${esc(l.compteDefaut)}</td>
          <td class="mono">${esc(l.compteReferentielV2)}</td>
          <td>${esc(l.ecartLibelle)} ${aValider}</td>
          <td><details id="${idAide}"><summary title="${esc(l.commentaire)}">Aide</summary><p style="max-width:320px;margin:6px 0 0;font-size:12px;line-height:1.5;">${esc(l.commentaire)}</p></details></td>
          <td>${restaurer}</td>
        </tr>`;
      })
      .join('');

    host.innerHTML = `
      <div class="card" id="${P}-panel" aria-labelledby="${P}-titre">
        <h2 class="card-title" id="${P}-titre">Comptes de paie</h2>
        ${bandeauExpertHtml()}
        <p style="${STYLE_AIDE}">
          Quand la paie est comptabilisée, chaque ligne (salaire, cotisation, impôt, net à payer) est écrite sur un compte.
          Le tableau montre le compte utilisé par votre entreprise, celui proposé par le logiciel et celui du référentiel comptable.
          ${deps.canMutate ? '' : '<strong>Lecture seule : seuls l’administrateur, le gestionnaire RH et le comptable peuvent modifier ces comptes.</strong>'}
        </p>
        <div id="${P}-resume" class="form-error-summary" role="alert" hidden></div>
        <div style="${STYLE_SCROLL}">
          <table class="data" id="${P}-table">
            <caption class="sr-only">Comptes utilisés pour les écritures de paie, un par ligne de paie</caption>
            <thead><tr>
              <th scope="col">Libellé</th><th scope="col">Sens</th><th scope="col">Compte utilisé</th>
              <th scope="col">Compte par défaut</th><th scope="col">Référentiel v2</th><th scope="col">Écart</th>
              <th scope="col">Aide</th><th scope="col">${deps.canMutate ? 'Action' : '<span class="sr-only">Action</span>'}</th>
            </tr></thead>
            <tbody>${corps}</tbody>
          </table>
        </div>
        <p id="${P}-application" style="${STYLE_AIDE}margin-top:12px;">${esc(MESSAGE_APPLICATION_COMPTES_PAIE)}</p>
        <p id="${P}-statut" role="status" style="${STYLE_AIDE}"></p>
        ${
          deps.canMutate
            ? `<div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:8px;">
                 <button type="button" class="btn btn-primary" id="${P}-enregistrer">Enregistrer les comptes de paie</button>
                 <button type="button" class="btn btn-secondary" id="${P}-reinitialiser">Réinitialiser aux valeurs par défaut</button>
               </div>`
            : ''
        }
      </div>`;
    bind();
  };

  const champs = (): HTMLInputElement[] =>
    Array.from(host.querySelectorAll<HTMLInputElement>('[data-compte-role]'));

  const lireSaisie = (): SaisieComptesPaie => {
    const saisie = saisieDepuisTable(table);
    for (const input of champs()) {
      const role = input.dataset['compteRole'] as RoleComptePaie;
      saisie[role] = input.value;
    }
    return saisie;
  };

  const afficher = (): ReturnType<typeof validerSaisieComptesPaie> => {
    const r = validerSaisieComptesPaie(lireSaisie());
    const erreurs = r.ok ? {} : r.erreurs;
    for (const input of champs()) {
      const role = input.dataset['compteRole'] as RoleComptePaie;
      majErreurChamp(host, input, `${P}-err-${role}`, erreurs[role]);
    }
    return r;
  };

  const bind = (): void => {
    const resume = host.querySelector<HTMLElement>(`#${P}-resume`);
    const statut = host.querySelector<HTMLElement>(`#${P}-statut`);

    for (const input of champs()) {
      input.addEventListener('input', () => {
        majResume(resume, null);
        afficher();
      });
    }

    host.querySelectorAll<HTMLButtonElement>('[data-retablir]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const role = btn.dataset['retablir'] as RoleComptePaie;
        const input = host.querySelector<HTMLInputElement>(`#${P}-in-${role}`);
        if (!input) return;
        input.value = compteParDefaut(role);
        afficher();
        majStatut(statut, `Compte rétabli à la valeur par défaut (${compteParDefaut(role)}). Pensez à enregistrer.`);
        input.focus();
      });
    });

    host.querySelector(`#${P}-enregistrer`)?.addEventListener('click', () => {
      void enregistrer(resume, statut);
    });
    host.querySelector(`#${P}-reinitialiser`)?.addEventListener('click', () => {
      void reinitialiser(resume, statut);
    });
  };

  const enregistrer = async (resume: HTMLElement | null, statut: HTMLElement | null): Promise<void> => {
    if (!deps.canMutate) return;
    const r = afficher();
    if (!r.ok) {
      majResume(resume, r.resume);
      const premier = champs().find((i) => i.getAttribute('aria-invalid') === 'true');
      premier?.focus();
      return;
    }
    const avant = table;
    try {
      table = await deps.save(r.table);
    } catch (err) {
      if (err instanceof ComptesPaieInvalideError) {
        majResume(resume, err.message);
        return;
      }
      deps.notify.error(err instanceof Error ? err.message : 'Enregistrement impossible.');
      return;
    }
    const modifications = (Object.keys(table) as RoleComptePaie[])
      .filter((role) => avant[role] !== table[role])
      .map((role) => ({ role, avant: avant[role], apres: table[role] }));
    await deps.audit(DESCRIPTION_AUDIT_COMPTES_PAIE, { modifications });
    render();
    host.querySelector<HTMLElement>(`#${P}-enregistrer`)?.focus();
    majStatut(host.querySelector<HTMLElement>(`#${P}-statut`) ?? statut, 'Comptes de paie enregistrés.');
    deps.notify.success('Comptes de paie enregistrés. Ils s’appliquent aux nouvelles écritures de paie.');
  };

  const reinitialiser = async (resume: HTMLElement | null, statut: HTMLElement | null): Promise<void> => {
    if (!deps.canMutate) return;
    const ok = await deps.confirm({
      title: 'Réinitialiser les comptes de paie',
      message:
        'Tous les comptes de paie reprendront les valeurs par défaut du logiciel. Les écritures déjà enregistrées ne sont pas modifiées. Continuer ?',
      confirmLabel: 'Réinitialiser',
      danger: true,
      host,
    });
    if (!ok) return;
    const avant = table;
    try {
      table = await deps.save({});
    } catch (err) {
      deps.notify.error(err instanceof Error ? err.message : 'Réinitialisation impossible.');
      return;
    }
    const modifications = (Object.keys(table) as RoleComptePaie[])
      .filter((role) => avant[role] !== table[role])
      .map((role) => ({ role, avant: avant[role], apres: table[role] }));
    await deps.audit(DESCRIPTION_AUDIT_COMPTES_PAIE, { reinitialisation: true, modifications });
    render();
    host.querySelector<HTMLElement>(`#${P}-reinitialiser`)?.focus();
    majResume(host.querySelector<HTMLElement>(`#${P}-resume`) ?? resume, null);
    majStatut(host.querySelector<HTMLElement>(`#${P}-statut`) ?? statut, 'Comptes de paie remis aux valeurs par défaut.');
    deps.notify.success('Comptes de paie remis aux valeurs par défaut.');
  };

  render();
}
